'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { ColumnFiltersState } from '@tanstack/table-core';
import { AdvancedFilter, Search } from '@morpho-org/ui-v6/client';
import { xor } from 'lodash-es';
import { memo, type PropsWithChildren, type SetStateAction, useCallback, useMemo } from 'react';
import type { DashboardActivityFilters as DashboardActivityFiltersType } from '@/helpers/filter/table';
import AdvancedNetworkFilter from '@/components/common/TableAdvancedFilters/AdvancedNetworkFilter';
import { useIsMobile } from '@/hooks/useBreakpoints';
import { trackFilterChanged } from '@/lib/analytics/client/trackers/filter';
import { useTableFiltersHandler } from '../../hooks/useTableFiltersHandler';
import { BORROW_COLUMN_IDS, DASHBOARD_BORROW_ACTIVITY_FILTER_OPTIONS } from '../config';
interface FilterProps {
  filters: ColumnFiltersState;
  setColumnFilters: (columnFilters: SetStateAction<ColumnFiltersState>) => void;
  availableChains?: ChainId[];
  availableTypes?: string[];
  withGlobalSearch: boolean;
  disabled?: boolean;
}
interface DashboardBorrowActivityFiltersProps extends PropsWithChildren {
  filterProps: FilterProps;
}
const SEARCH_DEBOUNCE_TIME = 500;
const DashboardBorrowActivityFilters = t0 => {
  const $ = _c(57);
  const {
    children,
    filterProps
  } = t0;
  const {
    filters,
    setColumnFilters,
    availableChains,
    availableTypes,
    withGlobalSearch,
    disabled: t1
  } = filterProps;
  const disabled = t1 === undefined ? false : t1;
  const isMobile = useIsMobile();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      chainIdsFilter: BORROW_COLUMN_IDS.Network,
      typeFilter: BORROW_COLUMN_IDS.Type,
      searchFilter: "global"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== setColumnFilters) {
    t3 = {
      setColumnFilters,
      filterMapping: t2
    };
    $[1] = setColumnFilters;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    handleFiltersChange
  } = useTableFiltersHandler(t3);
  let t4;
  if ($[3] !== filters) {
    t4 = filters.reduce(_temp, []);
    $[3] = filters;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const items = t4;
  const chainIdsFilter = items;
  let t5;
  if ($[5] !== filters) {
    t5 = filters.reduce(_temp2, []);
    $[5] = filters;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const items_0 = t5;
  const typeFilter = items_0;
  let t6;
  if ($[7] !== filters) {
    t6 = filters.find(_temp3);
    $[7] = filters;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const item = t6;
  const searchFilter = item?.value as string;
  let t7;
  if ($[9] !== handleFiltersChange || $[10] !== searchFilter || $[11] !== typeFilter) {
    t7 = chainIdsFilter_0 => {
      handleFiltersChange({
        chainIdsFilter: chainIdsFilter_0,
        searchFilter,
        typeFilter
      });
    };
    $[9] = handleFiltersChange;
    $[10] = searchFilter;
    $[11] = typeFilter;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleChainIdsFilterChange = t7;
  let t8;
  if ($[13] !== chainIdsFilter || $[14] !== handleFiltersChange || $[15] !== searchFilter) {
    t8 = typeFilter_0 => {
      handleFiltersChange({
        chainIdsFilter,
        searchFilter,
        typeFilter: typeFilter_0
      });
    };
    $[13] = chainIdsFilter;
    $[14] = handleFiltersChange;
    $[15] = searchFilter;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const handleTypeFilterChange = t8;
  let t9;
  if ($[17] !== chainIdsFilter || $[18] !== handleFiltersChange || $[19] !== typeFilter) {
    t9 = value => {
      handleFiltersChange({
        chainIdsFilter,
        searchFilter: value.trim(),
        typeFilter
      });
    };
    $[17] = chainIdsFilter;
    $[18] = handleFiltersChange;
    $[19] = typeFilter;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handleSearchChange = t9;
  let t10;
  if ($[21] !== availableTypes || $[22] !== typeFilter) {
    let t11;
    if ($[24] !== typeFilter) {
      t11 = option => ({
        id: option.value,
        label: option.label,
        value: option.value,
        isChecked: typeFilter.includes(option.value)
      });
      $[24] = typeFilter;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== availableTypes) {
      t12 = t13 => {
        const {
          value: value_0
        } = t13;
        return !availableTypes || availableTypes.includes(value_0);
      };
      $[26] = availableTypes;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    t10 = DASHBOARD_BORROW_ACTIVITY_FILTER_OPTIONS.map(t11).filter(t12);
    $[21] = availableTypes;
    $[22] = typeFilter;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const items_1 = t10;
  let t11;
  if ($[28] !== handleTypeFilterChange || $[29] !== typeFilter) {
    t11 = transactionType => {
      const updatedTypes = xor(typeFilter, [transactionType]);
      handleTypeFilterChange(updatedTypes);
      trackFilterChanged({
        filterName: "transaction_type",
        filterValue: updatedTypes,
        page: "dashboard_borrow_activity"
      });
    };
    $[28] = handleTypeFilterChange;
    $[29] = typeFilter;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const toggleTransactionType = t11;
  let t12;
  if ($[31] !== availableChains || $[32] !== chainIdsFilter || $[33] !== disabled || $[34] !== handleChainIdsFilterChange) {
    t12 = <AdvancedNetworkFilter disabled={disabled} chainIdsFilter={chainIdsFilter} handleChainIdsFilterChange={handleChainIdsFilterChange} availableChains={availableChains} page="dashboard_borrow_activity" />;
    $[31] = availableChains;
    $[32] = chainIdsFilter;
    $[33] = disabled;
    $[34] = handleChainIdsFilterChange;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-3xs text-foreground">Type:</span>;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== handleTypeFilterChange) {
    t14 = () => {
      handleTypeFilterChange([]);
      trackFilterChanged({
        filterName: "transaction_type",
        filterValue: [],
        page: "dashboard_borrow_activity"
      });
    };
    $[37] = handleTypeFilterChange;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== disabled || $[40] !== items_1 || $[41] !== t14 || $[42] !== toggleTransactionType) {
    t15 = <div className="gap-xs flex items-center">{t13}<AdvancedFilter items={items_1} toggleItem={toggleTransactionType} clear={t14} inputPlaceholder="Search for transaction type" clearTooltipText="Remove transaction type filter." disabled={disabled} /></div>;
    $[39] = disabled;
    $[40] = items_1;
    $[41] = t14;
    $[42] = toggleTransactionType;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== disabled || $[45] !== handleSearchChange || $[46] !== isMobile || $[47] !== searchFilter || $[48] !== withGlobalSearch) {
    t16 = <Search placeholder="Search tx hash or market" width={200} fullWidth={isMobile} value={searchFilter} onChange={handleSearchChange} disabled={disabled} debounceTime={SEARCH_DEBOUNCE_TIME} isGlobalSearchInput={withGlobalSearch} />;
    $[44] = disabled;
    $[45] = handleSearchChange;
    $[46] = isMobile;
    $[47] = searchFilter;
    $[48] = withGlobalSearch;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== t12 || $[51] !== t15 || $[52] !== t16) {
    t17 = <div className="gap-lg max-md:gap-sm flex flex-row items-center max-md:flex-wrap">{t12}{t15}{t16}</div>;
    $[50] = t12;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== children || $[55] !== t17) {
    t18 = <div className="border-border bg-card p-md max-md:gap-sm box-content flex min-h-[26px] flex-row content-center justify-between rounded-none rounded-t-[20px] border-[0.5px] border-b-0 max-md:box-border max-md:flex-wrap">{children}{t17}</div>;
    $[54] = children;
    $[55] = t17;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  return t18;
};
export default memo(DashboardBorrowActivityFilters);
function _temp(acc, filter) {
  if (filter.id === BORROW_COLUMN_IDS.Network) {
    acc.push(...(filter.value as ChainId[]));
  }
  return acc;
}
function _temp2(acc_0, filter_0) {
  if (filter_0.id === BORROW_COLUMN_IDS.Type) {
    acc_0.push(...(filter_0.value as string[]));
  }
  return acc_0;
}
function _temp3(filter_1) {
  return filter_1.id === "global";
}